import {
  DESIGN_HEIGHT,
  DESIGN_WIDTH,
  frontalQuad,
  horizontalQuad,
  project,
  toPoints,
} from '../geometry';
import type { Vec3 } from '../geometry';
import { boxFaces, scope, seeded, worldPoints } from './kit';
import { PAL } from './palette';
import { overlayQuad } from './regions';
import { WALL, wallCamera as cam, wallRegions } from './wall.geometry';
import type { ArtProps } from './types';

const { z: zWall, half, height, niche } = WALL;
const P = (x: number, y: number, z: number) => project(cam, [x, y, z]);
const zBack = zWall + niche.depth;

// ── Back: the room behind the stone ─────────────────────────────────────────────────────────────

export function WallBack({ ids }: ArtProps) {
  const { id, ref } = scope(ids, 'back');
  const top = P(0, height, zWall)[1];
  const floor = P(0, 0, zWall)[1];

  // floorboards run away from the viewer: their lines meet at the vanishing point
  const boards = Array.from({ length: 15 }, (_, index) => -3.5 + index * 0.5);

  return (
    <g>
      <defs>
        <linearGradient
          id={id('wall')}
          x1="0"
          y1={top}
          x2="0"
          y2={floor}
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#5a534b" />
          <stop offset="1" stopColor="#413c36" />
        </linearGradient>
        <linearGradient
          id={id('ceiling')}
          x1="0"
          y1="0"
          x2="0"
          y2={top}
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#171513" />
          <stop offset="1" stopColor="#2c2824" />
        </linearGradient>
        <linearGradient
          id={id('floor')}
          x1="0"
          y1={floor}
          x2="0"
          y2="800"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#2a2623" />
          <stop offset="1" stopColor="#0f0e0d" />
        </linearGradient>
      </defs>
      <rect width={DESIGN_WIDTH} height={DESIGN_HEIGHT} fill={ref('wall')} />
      <rect x="0" y="0" width={DESIGN_WIDTH} height={top} fill={ref('ceiling')} />
      <rect
        x="0"
        y={floor}
        width={DESIGN_WIDTH}
        height={DESIGN_HEIGHT - floor}
        fill={ref('floor')}
      />
      {/* floorboards */}
      <g stroke="#000" strokeOpacity="0.28" strokeWidth="1">
        {boards.map((x) => {
          const far = P(x, 0, zWall);
          const near = P(x, 0, 0.2);
          return <line key={x} x1={far[0]} y1={far[1]} x2={near[0]} y2={near[1]} />;
        })}
      </g>
      {/* cove light where the ceiling meets the wall */}
      <rect x="0" y={top - 2} width={DESIGN_WIDTH} height="3" fill={PAL.warm} opacity="0.9" />
      {/* skirting */}
      <rect x="0" y={floor - 6} width={DESIGN_WIDTH} height="6" fill="#171514" />
    </g>
  );
}

// ── Shade ───────────────────────────────────────────────────────────────────────────────────────

export function WallShade({ ids }: ArtProps) {
  const { id } = scope(ids, 'shade');
  const [panel] = wallRegions;
  const top = P(0, height, zWall)[1];
  const floor = P(0, 0, zWall)[1];
  const nicheBottom = P(0, niche.y0, zWall)[1];
  return (
    <g>
      <defs>
        <linearGradient
          id={id('vertical')}
          x1="0"
          y1={top}
          x2="0"
          y2={floor}
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#000" stopOpacity="0.18" />
          <stop offset="0.35" stopColor="#000" stopOpacity="0" />
          <stop offset="0.75" stopColor="#000" stopOpacity="0.12" />
          <stop offset="1" stopColor="#000" stopOpacity="0.5" />
        </linearGradient>
        <radialGradient
          id={id('vignette')}
          cx="600"
          cy="380"
          r="520"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(600 380) scale(1 0.8) translate(-600 -380)"
        >
          <stop offset="0.6" stopColor="#000" stopOpacity="0" />
          <stop offset="1" stopColor="#000" stopOpacity="0.5" />
        </radialGradient>
        <linearGradient
          id={id('under')}
          x1="0"
          y1={nicheBottom}
          x2="0"
          y2={nicheBottom + 70}
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#000" stopOpacity="0.4" />
          <stop offset="1" stopColor="#000" stopOpacity="0" />
        </linearGradient>
      </defs>
      {panel ? (
        <>
          <polygon points={toPoints(overlayQuad(panel))} fill={`url(#${id('vertical')})`} />
          <polygon points={toPoints(overlayQuad(panel))} fill={`url(#${id('vignette')})`} />
          <rect
            x={P(niche.x0, 0, zWall)[0] - 20}
            y={nicheBottom}
            width={P(niche.x1, 0, zWall)[0] - P(niche.x0, 0, zWall)[0] + 40}
            height="70"
            fill={`url(#${id('under')})`}
          />
        </>
      ) : null}
    </g>
  );
}

// ── Sheen ───────────────────────────────────────────────────────────────────────────────────────

export function WallSheen({ ids }: ArtProps) {
  const { id } = scope(ids, 'sheen');
  const top = P(0, height, zWall)[1];
  const floor = P(0, 0, zWall)[1];
  const n0 = P(niche.x0, niche.y1, zWall);
  const n1 = P(niche.x1, niche.y0, zWall);
  const consoleGlowY = P(0, 0.34, zWall)[1];
  return (
    <g>
      <defs>
        <filter id={id('soft')} x="-30%" y="-60%" width="160%" height="220%">
          <feGaussianBlur stdDeviation="26" />
        </filter>
        <filter id={id('sharp')} x="-30%" y="-60%" width="160%" height="220%">
          <feGaussianBlur stdDeviation="9" />
        </filter>
        <linearGradient
          id={id('cove')}
          x1="0"
          y1={top}
          x2="0"
          y2={top + 260}
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor={PAL.warmHot} stopOpacity="0.95" />
          <stop offset="0.4" stopColor={PAL.warm} stopOpacity="0.35" />
          <stop offset="1" stopColor={PAL.warm} stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* grazing light from the cove: it rakes down the stone and shows its grain */}
      <rect x="0" y={top} width={DESIGN_WIDTH} height="270" fill={`url(#${id('cove')})`} />
      {/* the niche's own glow on the stone around it */}
      <rect
        x={n0[0] - 18}
        y={n0[1] - 12}
        width={n1[0] - n0[0] + 36}
        height={n1[1] - n0[1] + 24}
        rx="10"
        fill={PAL.warm}
        opacity="0.9"
        filter={`url(#${id('soft')})`}
      />
      {/* light under the floating console */}
      <rect
        x={n0[0] - 10}
        y={consoleGlowY - 4}
        width={n1[0] - n0[0] + 20}
        height="16"
        fill={PAL.warm}
        opacity="0.85"
        filter={`url(#${id('sharp')})`}
      />
      {/* polished sheen */}
      <polygon
        points={`300,${top} 470,${top} 250,${floor} 80,${floor}`}
        fill="#fff"
        opacity="0.14"
        filter={`url(#${id('soft')})`}
      />
      <polygon
        points={`860,${top} 930,${top} 770,${floor} 700,${floor}`}
        fill="#fff"
        opacity="0.1"
        filter={`url(#${id('soft')})`}
      />
    </g>
  );
}

// ── Front: the niche, the console and the room in front of the wall ─────────────────────────────

export function WallFront({ ids }: ArtProps) {
  const { id, ref } = scope(ids, 'front');
  const rng = seeded(8);
  const top = P(0, height, zWall)[1];

  // panel inlay: a brass line along both edges of the stone
  const left = P(-half, 0, zWall);
  const leftTop = P(-half, height, zWall);
  const right = P(half, 0, zWall);
  const rightTop = P(half, height, zWall);

  // niche faces
  const opening = frontalQuad(cam, niche.x0, niche.x1, niche.y0, niche.y1, zWall);
  const back = frontalQuad(cam, niche.x0, niche.x1, niche.y0, niche.y1, zBack);
  const roof = horizontalQuad(cam, niche.x0, niche.x1, zWall, zBack, niche.y1);
  const ledge = horizontalQuad(cam, niche.x0, niche.x1, zWall, zBack, niche.y0);
  const sideL: Vec3[] = [
    [niche.x0, niche.y1, zWall],
    [niche.x0, niche.y1, zBack],
    [niche.x0, niche.y0, zBack],
    [niche.x0, niche.y0, zWall],
  ];
  const sideR: Vec3[] = [
    [niche.x1, niche.y1, zBack],
    [niche.x1, niche.y1, zWall],
    [niche.x1, niche.y0, zWall],
    [niche.x1, niche.y0, zBack],
  ];

  // things on the niche ledge
  const vase = P(-0.75, niche.y0, zWall + 0.22);
  const bowl = P(0.05, niche.y0, zWall + 0.22);
  const books = P(0.85, niche.y0, zWall + 0.22);
  const ledgeScale = 900 / (zWall + 0.22 + 0.9);

  // console
  const consoleBox = boxFaces(cam, { x0: -1.7, x1: 1.7, y0: 0.38, y1: 0.74, z0: 3.62, z1: zWall });

  // coffee table in the foreground
  const table = Array.from({ length: 40 }, (_, index) => {
    const angle = (index / 40) * Math.PI * 2;
    return P(Math.cos(angle) * 0.62, 0.42, 2.0 + Math.sin(angle) * 0.4);
  });
  const tableUnder = table.map(([x, y]) => [x, y + 7] as const);
  const tableScale = 900 / (2.0 + 0.9);

  // plant, right
  const pot = P(2.55, 0, 3.3);
  const potTop = P(2.55, 0.46, 3.3);
  const plantScale = 900 / (3.3 + 0.9);
  const leaves = Array.from({ length: 22 }, (_, index) => {
    const angle = -90 + (index - 10.5) * 11 + (rng() - 0.5) * 14;
    const rad = (angle * Math.PI) / 180;
    const length = (0.55 + rng() * 0.9) * plantScale * 1.1;
    const baseX = potTop[0];
    const baseY = potTop[1] - rng() * 40;
    const tipX = baseX + Math.cos(rad) * length;
    const tipY = baseY + Math.sin(rad) * length;
    const width = (0.1 + rng() * 0.07) * plantScale;
    const midX = (baseX + tipX) / 2;
    const midY = (baseY + tipY) / 2;
    return {
      d: `M${baseX.toFixed(1)} ${baseY.toFixed(1)}Q${(midX - Math.sin(rad) * width).toFixed(1)} ${(midY + Math.cos(rad) * width).toFixed(1)} ${tipX.toFixed(1)} ${tipY.toFixed(1)}Q${(midX + Math.sin(rad) * width).toFixed(1)} ${(midY - Math.cos(rad) * width).toFixed(1)} ${baseX.toFixed(1)} ${baseY.toFixed(1)}Z`,
      fill: index % 3 === 0 ? PAL.leafHi : index % 3 === 1 ? PAL.leaf : PAL.leafLo,
    };
  });

  // floor lamp, left
  const lampBase = P(-2.55, 0, 3.1);
  const lampTop = P(-2.55, 1.9, 3.1);
  const lampHead = P(-1.95, 1.8, 3.1);
  const lampScale = 900 / (3.1 + 0.9);

  return (
    <g>
      <defs>
        <linearGradient id={id('back')} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#3a2a1a" />
          <stop offset="0.5" stopColor="#6a4d2c" />
          <stop offset="1" stopColor="#3a2a1a" />
        </linearGradient>
        <linearGradient
          id={id('led')}
          x1="0"
          y1={opening[0][1]}
          x2="0"
          y2={opening[3][1]}
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor={PAL.warmHot} stopOpacity="0.95" />
          <stop offset="0.3" stopColor={PAL.warm} stopOpacity="0.3" />
          <stop offset="1" stopColor={PAL.warm} stopOpacity="0" />
        </linearGradient>
        <linearGradient id={id('console')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#3b2d21" />
          <stop offset="1" stopColor="#1d1510" />
        </linearGradient>
        <linearGradient id={id('wood')} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#4a3828" />
          <stop offset="1" stopColor="#1f1610" />
        </linearGradient>
        <linearGradient id={id('metal')} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor={PAL.brassLo} />
          <stop offset="0.45" stopColor={PAL.brassHi} />
          <stop offset="1" stopColor={PAL.brass} />
        </linearGradient>
        <linearGradient id={id('pot')} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#d6c9ac" />
          <stop offset="1" stopColor="#9c8e70" />
        </linearGradient>
        <radialGradient id={id('glow')} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor={PAL.warm} stopOpacity="0.8" />
          <stop offset="1" stopColor={PAL.warm} stopOpacity="0" />
        </radialGradient>
        <radialGradient
          id={id('vignette')}
          cx="600"
          cy="400"
          r="800"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(600 400) scale(1 0.72) translate(-600 -400)"
        >
          <stop offset="0.55" stopColor="#000" stopOpacity="0" />
          <stop offset="1" stopColor="#000" stopOpacity="0.55" />
        </radialGradient>
        <filter id={id('shadow')} x="-30%" y="-60%" width="160%" height="220%">
          <feGaussianBlur stdDeviation="9" />
        </filter>
      </defs>

      {/* brass inlay framing the stone */}
      <line
        x1={left[0]}
        y1={leftTop[1]}
        x2={left[0]}
        y2={left[1]}
        stroke={ref('metal')}
        strokeWidth="3"
      />
      <line
        x1={right[0]}
        y1={rightTop[1]}
        x2={right[0]}
        y2={right[1]}
        stroke={ref('metal')}
        strokeWidth="3"
      />
      <line
        x1={left[0]}
        y1={top}
        x2={right[0]}
        y2={top}
        stroke="#000"
        strokeOpacity="0.4"
        strokeWidth="1.5"
      />

      {/* niche */}
      <polygon points={toPoints(back)} fill={ref('back')} />
      <polygon points={worldPoints(cam, sideL)} fill="#241a10" />
      <polygon points={worldPoints(cam, sideR)} fill="#241a10" />
      <polygon points={toPoints(roof)} fill="#120c07" />
      <polygon points={toPoints(ledge)} fill="#7a5a34" />
      <polygon points={toPoints(opening)} fill={ref('led')} />
      <polyline
        points={toPoints([opening[3], opening[0], opening[1], opening[2]])}
        fill="none"
        stroke={PAL.brass}
        strokeOpacity="0.8"
        strokeWidth="1.5"
      />
      {/* ledge objects */}
      <ellipse
        cx={vase[0]}
        cy={vase[1] - 1}
        rx={ledgeScale * 0.1}
        ry="3"
        fill="#000"
        opacity="0.5"
      />
      <path
        d={`M${vase[0] - ledgeScale * 0.06} ${vase[1]}Q${vase[0] - ledgeScale * 0.12} ${vase[1] - ledgeScale * 0.25} ${vase[0] - ledgeScale * 0.04} ${vase[1] - ledgeScale * 0.42}L${vase[0] + ledgeScale * 0.04} ${vase[1] - ledgeScale * 0.42}Q${vase[0] + ledgeScale * 0.12} ${vase[1] - ledgeScale * 0.25} ${vase[0] + ledgeScale * 0.06} ${vase[1]}Z`}
        fill={PAL.bone}
      />
      <path
        d={`M${bowl[0] - ledgeScale * 0.2} ${bowl[1] - ledgeScale * 0.1}Q${bowl[0] - ledgeScale * 0.16} ${bowl[1]} ${bowl[0]} ${bowl[1]}Q${bowl[0] + ledgeScale * 0.16} ${bowl[1]} ${bowl[0] + ledgeScale * 0.2} ${bowl[1] - ledgeScale * 0.1}Z`}
        fill={ref('metal')}
      />
      {[0, 1, 2].map((index) => (
        <rect
          key={index}
          x={books[0] - ledgeScale * (0.22 - index * 0.015)}
          y={books[1] - ledgeScale * 0.055 * (index + 1)}
          width={ledgeScale * (0.44 - index * 0.03)}
          height={ledgeScale * 0.05}
          fill={['#d4cbb8', '#2c2a28', '#8c6a43'][index]}
        />
      ))}

      {/* floating console */}
      <g filter={`url(#${id('shadow')})`}>
        <rect
          x={P(-1.7, 0, zWall)[0]}
          y={P(0, 0.34, zWall)[1] + 8}
          width={P(1.7, 0, zWall)[0] - P(-1.7, 0, zWall)[0]}
          height="22"
          fill="#000"
          opacity="0.35"
        />
      </g>
      <polygon points={toPoints(consoleBox.front)} fill={ref('console')} />
      {consoleBox.top ? <polygon points={toPoints(consoleBox.top)} fill={ref('wood')} /> : null}
      <polyline
        points={toPoints([consoleBox.front[3] ?? [0, 0], consoleBox.front[2] ?? [0, 0]])}
        fill="none"
        stroke={PAL.warm}
        strokeWidth="2"
        strokeOpacity="0.85"
      />

      {/* coffee table */}
      <ellipse
        cx="600"
        cy="768"
        rx={tableScale * 0.7}
        ry="30"
        fill="#000"
        opacity="0.5"
        filter={`url(#${id('shadow')})`}
      />
      <polygon points={toPoints(tableUnder)} fill="#0f0a07" />
      <polygon points={toPoints(table)} fill={ref('wood')} />
      <polyline
        points={toPoints(table)}
        fill="none"
        stroke={PAL.brass}
        strokeOpacity="0.6"
        strokeWidth="1.3"
      />

      {/* floor lamp */}
      <ellipse
        cx={lampBase[0]}
        cy={lampBase[1]}
        rx={lampScale * 0.18}
        ry={lampScale * 0.04}
        fill="#161517"
      />
      <path
        d={`M${lampBase[0]} ${lampBase[1]}L${lampTop[0]} ${lampTop[1]}Q${lampTop[0] + 30} ${lampTop[1] - 60} ${lampHead[0]} ${lampHead[1]}`}
        fill="none"
        stroke={PAL.brassLo}
        strokeWidth="4"
        strokeLinecap="round"
      />
      <ellipse cx={lampHead[0]} cy={lampHead[1] + 24} rx="64" ry="48" fill={ref('glow')} />
      <path
        d={`M${lampHead[0] - 22} ${lampHead[1] + 18}Q${lampHead[0]} ${lampHead[1] - 16} ${lampHead[0] + 22} ${lampHead[1] + 18}Z`}
        fill={ref('metal')}
      />

      {/* plant */}
      <ellipse
        cx={pot[0]}
        cy={pot[1] + 2}
        rx={plantScale * 0.4}
        ry="8"
        fill="#000"
        opacity="0.5"
        filter={`url(#${id('shadow')})`}
      />
      {leaves.map((leaf, index) => (
        <path key={index} d={leaf.d} fill={leaf.fill} />
      ))}
      <path
        d={`M${potTop[0] - plantScale * 0.24} ${potTop[1]}L${potTop[0] + plantScale * 0.24} ${potTop[1]}L${pot[0] + plantScale * 0.17} ${pot[1]}L${pot[0] - plantScale * 0.17} ${pot[1]}Z`}
        fill={ref('pot')}
      />

      <rect width={DESIGN_WIDTH} height={DESIGN_HEIGHT} fill={ref('vignette')} />
    </g>
  );
}
